/** @format */

import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RegistryRole, RoleRegistryService } from 'src/app/services/workflow/role-registry.service';
import { UserService } from 'src/app/services/user/user.service';
import {
  AssignmentRoleUser,
  WorkflowAssignmentResponse,
  WorkflowService,
} from 'src/app/services/workflow/workflow.service';
import { ToastService } from 'src/app/services/toast/toast.service';

/** A user that can be assigned to act as an actor role. */
interface AssignableUser {
  /** Stable composite key ("U:S:<subject>" / "U:L:<id>" / "T:<id>") since ids can collide or be absent. */
  key: string;
  userId: number | null;
  userSubjectId: string | null;
  userType: 'USER' | 'TEMP_USER';
  colorSeed: number;
  name: string;
  email: string;
}

/**
 * View-model wrapping a registry role with the assignment metadata the redesigned
 * panel renders. `assignKind` decides whether the role is filled by users or groups
 * (Initiator stages take groups; approver stages take users).
 */
interface RoleRow extends RegistryRole {
  assignKind: 'users' | 'groups';
}

type StatusFilter = 'all' | 'active' | 'inactive';

@Component({
  standalone: false,
  selector: 'app-role-registry',
  templateUrl: './role-registry.component.html',
  styleUrls: ['./role-registry.component.css'],
})
export class RoleRegistryComponent implements OnChanges {
  /** Controls modal visibility. */
  @Input() open = false;
  /** The workflow being configured — its assigned groups scope the user picker. */
  @Input() workflowTemplateId: number | null = null;
  /** Emitted when the modal is dismissed (host should refresh its role list). */
  @Output() closed = new EventEmitter<void>();

  roles: RoleRow[] = [];
  search = '';
  loading = false;

  /** Status filter driven by the toolbar Filter dropdown. */
  statusFilter: StatusFilter = 'all';
  filterMenuOpen = false;

  /** Id of the role whose row-actions (kebab) menu is open, if any. */
  menuRoleId: string | null = null;
  /** Viewport coords for the open kebab menu (fixed-positioned to escape table overflow clipping). */
  menuTop = 0;
  menuLeft = 0;

  /** ---- Edit / New role dialog ---- */
  editOpen = false;
  editMode: 'create' | 'edit' = 'create';
  editId: string | null = null;
  editName = '';
  editDescription = '';
  editActive = true;
  editError = '';

  /** ---- Manage user assignments dialog ---- */
  assignOpen = false;
  assignRole: RoleRow | null = null;
  assignSearch = '';
  assignSelected = new Set<string>();

  /** ---- Deactivate / activate confirmation dialog ---- */
  confirmOpen = false;
  confirmRole: RoleRow | null = null;

  /** ---- Remove-users confirmation (only shown when an assignment removes users) ---- */
  removeConfirmOpen = false;
  removeConfirmCount = 0;
  assignSaving = false;

  /** Users belonging to this workflow's assigned groups (the only valid actors). */
  allUsers: AssignableUser[] = [];
  usersLoading = false;
  /** True when the workflow has no assignment yet — the picker is empty by design. */
  noAssignment = false;
  /** Ids of rows expanded to show their assigned users. */
  expandedRoleIds = new Set<string>();

  private assignedByRole = new Map<string, Set<string>>();
  private templateUsageByRole = new Map<string, number>();
  /** Every tenant assignment of this workflow — each is a separate account-holder/client pair. */
  assignments: WorkflowAssignmentResponse[] = [];
  /** The tenant assignment currently being configured (role→user records are saved against it). */
  assignmentId: number | null = null;

  constructor(
    private roleRegistry: RoleRegistryService,
    private userService: UserService,
    private workflowService: WorkflowService,
    public toastService: ToastService
  ) {}

  private toastTimer: any = null;
  private showToast(text: string, isError = false): void {
    this.toastService.toastText = text;
    this.toastService.isError = isError;
    this.toastService.showToast = true;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastService.showToast = false), 3000);
  }

  ngOnChanges(): void {
    if (this.open) {
      this.loading = true;
      this.expandedRoleIds.clear();
      this.assignedByRole.clear();
      // Load the assigned-group users first, then the role catalog, then assignments.
      this.loadAssignedGroupUsers(() => {
        this.roleRegistry.load().subscribe({
          next: () => {
            this.loading = false;
            this.refresh();
            this.loadRoleUsers();
          },
          error: () => {
            this.loading = false;
            this.refresh();
          },
        });
      });
    }
  }

  /**
   * Load the users who belong to the groups assigned to this workflow. Regular
   * groups resolve to plant users (filtered by their group membership); temp
   * groups resolve to temp users (filtered by their temp-group id).
   */
  private loadAssignedGroupUsers(done: () => void): void {
    this.usersLoading = true;
    this.noAssignment = false;
    this.allUsers = [];
    this.assignments = [];

    if (this.workflowTemplateId == null) {
      this.noAssignment = true;
      this.usersLoading = false;
      done();
      return;
    }

    this.workflowService.getAssignments(this.workflowTemplateId).subscribe({
      next: (assignments) => {
        this.assignments = assignments || [];
        if (this.assignments.length === 0) {
          this.assignmentId = null;
          this.noAssignment = true;
          this.usersLoading = false;
          done();
          return;
        }

        // Keep the previously selected assignment if it still exists, else default to the first.
        const current = this.assignments.find((a) => a.workflowAssignmentId === this.assignmentId);
        const assignment = current ?? this.assignments[0];
        this.assignmentId = assignment.workflowAssignmentId;
        this.buildUsersForAssignment(assignment, done);
      },
      error: () => {
        this.noAssignment = true;
        this.usersLoading = false;
        done();
      },
    });
  }

  /** Resolve the assignable users (regular + temp) for one tenant assignment's groups. */
  private buildUsersForAssignment(assignment: WorkflowAssignmentResponse, done: () => void): void {
    this.usersLoading = true;
    this.allUsers = [];
    {
        const plantId = assignment.clientPlantId;
        const userGroupIds = new Set(
          assignment.groups.filter((g) => g.groupType === 'USER_GROUP').map((g) => g.groupId)
        );
        const tempGroupIds = new Set(
          assignment.groups.filter((g) => g.groupType === 'TEMP_USER_GROUP').map((g) => g.groupId)
        );

        const req = { pageNumber: 1, pageSize: 500, search: '', plantId };
        forkJoin([
          userGroupIds.size
            ? this.userService.retrieveUsers(req).pipe(catchError(() => of([] as any)))
            : of([] as any),
          tempGroupIds.size
            ? this.userService.retrieveTempUsers(req).pipe(catchError(() => of([] as any)))
            : of([] as any),
        ]).subscribe({
          next: ([usersRes, tempRes]) => {
            const users: any[] = Array.isArray(usersRes) ? usersRes : usersRes?.item1 ?? [];
            const temps: any[] = Array.isArray(tempRes) ? tempRes : tempRes?.item1 ?? [];

            const regular: AssignableUser[] = users
              .filter((u) => !u.isDeleted)
              .filter((u) => (u.group || []).some((g: any) => userGroupIds.has(g.groupId)))
              .map((u) => ({
                key: this.regularUserKey(u.subjectId, u.userId),
                userId: this.normalizeUserId(u.userId),
                userSubjectId: this.normalizeSubjectId(u.subjectId),
                userType: 'USER',
                colorSeed: this.colorSeedFor(u.subjectId, u.userId),
                name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email,
                email: u.email,
              }));

            const tempUsers: AssignableUser[] = temps
              .filter((u) => !u.isDeleted)
              .filter((u) => tempGroupIds.has(u.tempUserGroupId))
              .map((u) => ({
                key: `T:${u.tempUserId}`,
                userId: this.normalizeUserId(u.tempUserId),
                userSubjectId: null,
                userType: 'TEMP_USER',
                colorSeed: u.tempUserId,
                name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email,
                email: u.email,
              }));

            // De-dupe by key (a user can be in several assigned groups).
            const seen = new Set<string>();
            this.allUsers = [...regular, ...tempUsers].filter((u) => {
              if (seen.has(u.key)) return false;
              seen.add(u.key);
              return true;
            });
            this.usersLoading = false;
            done();
          },
          error: () => {
            this.allUsers = [];
            this.usersLoading = false;
            done();
          },
        });
    }
  }

  /** True when this workflow has more than one tenant assignment to choose between. */
  get hasMultipleAssignments(): boolean {
    return this.assignments.length > 1;
  }

  /** Human label for an assignment dropdown option (account holder — client). */
  assignmentLabel(a: WorkflowAssignmentResponse): string {
    const company = a.accountHolderCompanyName || `Company ${a.accountHolderCompanyId}`;
    const client = a.clientPlantName || `Client ${a.clientPlantId}`;
    return `${company} — ${client}`;
  }

  /** Switch the panel to a different tenant assignment, reloading its users and role mappings. */
  onAssignmentChange(assignmentId: number): void {
    const assignment = this.assignments.find((a) => a.workflowAssignmentId === Number(assignmentId));
    if (!assignment) return;
    this.assignmentId = assignment.workflowAssignmentId;
    this.assignedByRole.clear();
    this.buildUsersForAssignment(assignment, () => this.loadRoleUsers());
  }

  /** Load the persisted role → user assignments for the current tenant assignment. */
  private loadRoleUsers(): void {
    if (this.workflowTemplateId == null || this.assignmentId == null) return;
    this.workflowService.getRoleUsers(this.workflowTemplateId, this.assignmentId).subscribe({
      next: (roleUsers) => {
        const roleNameById = new Map<number, string>();
        this.roles.forEach((r) => roleNameById.set(r.roleId, r.id));
        this.assignedByRole.clear();
        (roleUsers || []).forEach((ru) => {
          const roleName = roleNameById.get(ru.workflowActorRoleId);
          if (!roleName) return;
          this.assignedByRole.set(roleName, new Set(ru.users.map((u) => this.userToKey(u))));
        });
      },
    });
  }

  private userToKey(u: AssignmentRoleUser): string {
    return u.userType === 'TEMP_USER'
      ? `T:${u.userId}`
      : this.regularUserKey(u.userSubjectId, u.userId);
  }

  private keyToUser(key: string): AssignmentRoleUser {
    const user = this.allUsers.find((u) => u.key === key);
    if (user) {
      return user.userType === 'TEMP_USER'
        ? { userId: user.userId, userType: 'TEMP_USER' }
        : { userId: user.userSubjectId ? null : user.userId, userSubjectId: user.userSubjectId, userType: 'USER' };
    }

    const [prefix, idKind, value] = key.split(':');
    if (prefix === 'T') return { userId: Number(idKind), userType: 'TEMP_USER' };
    return idKind === 'S'
      ? { userId: null, userSubjectId: value, userType: 'USER' }
      : { userId: Number(value), userType: 'USER' };
  }

  private regularUserKey(subjectId: unknown, userId: unknown): string {
    const normalizedSubjectId = this.normalizeSubjectId(subjectId);
    if (normalizedSubjectId) return `U:S:${normalizedSubjectId}`;
    return `U:L:${this.normalizeUserId(userId) ?? 0}`;
  }

  private normalizeSubjectId(subjectId: unknown): string | null {
    const value = String(subjectId ?? '').trim();
    return value.length ? value : null;
  }

  private normalizeUserId(userId: unknown): number | null {
    const value = Number(userId);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private colorSeedFor(subjectId: unknown, userId: unknown): number {
    const localId = this.normalizeUserId(userId);
    if (localId != null) return localId;

    const subject = this.normalizeSubjectId(subjectId) ?? '';
    return Array.from(subject).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  }

  close(): void {
    this.search = '';
    this.statusFilter = 'all';
    this.filterMenuOpen = false;
    this.menuRoleId = null;
    this.editOpen = false;
    this.assignOpen = false;
    this.confirmOpen = false;
    this.closed.emit();
  }

  /** Dismiss any open popovers when clicking elsewhere in the document. */
  @HostListener('document:click')
  onDocumentClick(): void {
    this.menuRoleId = null;
    this.filterMenuOpen = false;
  }

  // Canonical workflow stage-role order; everything else falls in after, alphabetically.
  private static readonly ROLE_ORDER = ['initiator', 'first approver', 'second approver'];

  private refresh(): void {
    this.roles = this.roleRegistry.getAllRoles().map((r) => ({
      ...r,
      assignKind: this.kindFor(r),
    }));
    const rank = (label: string) => {
      const i = RoleRegistryComponent.ROLE_ORDER.indexOf((label || '').trim().toLowerCase());
      return i === -1 ? RoleRegistryComponent.ROLE_ORDER.length : i;
    };
    this.roles.sort((a, b) => rank(a.label) - rank(b.label) || a.label.localeCompare(b.label));
    this.seedMockData();
  }

  private kindFor(r: RegistryRole): 'users' | 'groups' {
    return r.label.toLowerCase().includes('initiator') ? 'groups' : 'users';
  }

  /**
   * Roles start with NO users assigned — admins assign users explicitly via the
   * Manage User Assignments dialog. Only the template-usage figure (shown in the
   * deactivate dialog) is seeded until the API exposes it.
   */
  private seedMockData(): void {
    const usage = [4, 3, 2, 0, 1];
    this.roles.forEach((r, i) => {
      if (!this.templateUsageByRole.has(r.id)) {
        this.templateUsageByRole.set(r.id, usage[i % usage.length]);
      }
    });
  }

  get filteredRoles(): RoleRow[] {
    const q = this.search.trim().toLowerCase();
    return this.roles.filter((r) => {
      if (this.statusFilter === 'active' && !r.active) return false;
      if (this.statusFilter === 'inactive' && r.active) return false;
      if (!q) return true;
      return r.label.toLowerCase().includes(q) || r.description.toLowerCase().includes(q);
    });
  }

  get activeCount(): number {
    return this.roles.filter((r) => r.active).length;
  }
  get inactiveCount(): number {
    return this.roles.filter((r) => !r.active).length;
  }

  /** ---------- Assignment helpers (used by the table + dialogs) ---------- */

  assignedLabel(role: RoleRow): string {
    const n = this.assignedByRole.get(role.id)?.size ?? 0;
    return `${n} ${n === 1 ? 'User' : 'Users'}`;
  }

  templateUsage(role: RoleRow | null): number {
    return role ? this.templateUsageByRole.get(role.id) ?? 0 : 0;
  }

  initials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join('');
  }

  avatarColor(id: number): string {
    const palette = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
    return palette[id % palette.length];
  }

  /** Users currently assigned to a role (resolved against the loaded user list). */
  assignedUsers(role: RoleRow): AssignableUser[] {
    const keys = this.assignedByRole.get(role.id);
    if (!keys) return [];
    return this.allUsers.filter((u) => keys.has(u.key));
  }

  /** ---------- Expandable rows ---------- */

  toggleExpand(role: RoleRow, event: MouseEvent): void {
    event.stopPropagation();
    if (this.expandedRoleIds.has(role.id)) this.expandedRoleIds.delete(role.id);
    else this.expandedRoleIds.add(role.id);
  }
  isExpanded(role: RoleRow): boolean {
    return this.expandedRoleIds.has(role.id);
  }

  /** ---------- Row actions menu ---------- */

  toggleMenu(role: RoleRow, event: MouseEvent): void {
    event.stopPropagation();
    if (this.menuRoleId === role.id) {
      this.menuRoleId = null;
      return;
    }
    // Position the menu in viewport coords so table/body overflow can't clip it.
    const btn = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 150;
    this.menuLeft = Math.max(8, btn.right - menuWidth);
    const below = btn.bottom + 6;
    // Flip above the button when it would overflow the viewport bottom.
    this.menuTop = below + menuHeight > window.innerHeight ? btn.top - menuHeight - 6 : below;
    this.menuRoleId = role.id;
  }

  /** Close transient popovers when the modal body scrolls (fixed menus would drift). */
  closeMenusOnScroll(): void {
    this.menuRoleId = null;
    this.filterMenuOpen = false;
  }

  toggleFilterMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.filterMenuOpen = !this.filterMenuOpen;
  }

  setFilter(value: StatusFilter, event: MouseEvent): void {
    event.stopPropagation();
    this.statusFilter = value;
    this.filterMenuOpen = false;
  }

  /** ---------- Edit / New role dialog ---------- */

  openCreate(): void {
    this.editMode = 'create';
    this.editId = null;
    this.editName = '';
    this.editDescription = '';
    this.editActive = true;
    this.editError = '';
    this.editOpen = true;
  }

  openEdit(role: RoleRow): void {
    this.menuRoleId = null;
    this.editMode = 'edit';
    this.editId = role.id;
    this.editName = role.label;
    this.editDescription = role.description;
    this.editActive = role.active;
    this.editError = '';
    this.editOpen = true;
  }

  saveEdit(): void {
    this.editError = '';
    const name = this.editName.trim();
    if (!name) {
      this.editError = 'Role name is required';
      return;
    }

    if (this.editMode === 'create') {
      this.roleRegistry.addRole(name, this.editDescription).subscribe({
        next: () => {
          this.refresh();
          this.editOpen = false;
        },
        error: (err) => {
          this.editError = err?.error?.message || 'A role with this name already exists';
        },
      });
      return;
    }

    const role = this.roles.find((r) => r.id === this.editId);
    if (!role) {
      this.editOpen = false;
      return;
    }
    this.roleRegistry
      .updateRole(role.roleId, name, this.editDescription, this.editActive)
      .subscribe({
        next: () => {
          this.refresh();
          this.editOpen = false;
        },
        error: (err) => {
          this.editError = err?.error?.message || 'Unable to save the role.';
        },
      });
  }

  cancelEdit(): void {
    this.editOpen = false;
    this.editError = '';
  }

  /** ---------- Manage user assignments dialog ---------- */

  openAssignments(role: RoleRow): void {
    this.menuRoleId = null;
    this.assignRole = role;
    this.assignSearch = '';
    this.assignSelected = new Set(this.assignedByRole.get(role.id) ?? []);
    this.assignOpen = true;
  }

  get assignFilteredUsers(): AssignableUser[] {
    const q = this.assignSearch.trim().toLowerCase();
    if (!q) return this.allUsers;
    return this.allUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }

  get assignSelectedUsers(): AssignableUser[] {
    return this.allUsers.filter((u) => this.assignSelected.has(u.key));
  }

  isAssigned(user: AssignableUser): boolean {
    return this.assignSelected.has(user.key);
  }

  toggleAssigned(user: AssignableUser): void {
    if (this.assignSelected.has(user.key)) this.assignSelected.delete(user.key);
    else this.assignSelected.add(user.key);
  }

  /** Bulk-add every user currently visible in the left (filtered) pane. */
  selectAllFiltered(): void {
    this.assignFilteredUsers.forEach((u) => this.assignSelected.add(u.key));
  }

  /** Bulk-remove every user currently visible in the left (filtered) pane. */
  deselectAllFiltered(): void {
    this.assignFilteredUsers.forEach((u) => this.assignSelected.delete(u.key));
  }

  clearAssigned(): void {
    this.assignSelected.clear();
  }

  saveAssignments(): void {
    const role = this.assignRole;
    if (!role) {
      this.assignOpen = false;
      return;
    }
    // Confirm only the destructive case: removing one or more already-assigned users.
    const removed = this.removedCount(role);
    if (removed > 0) {
      this.removeConfirmCount = removed;
      this.removeConfirmOpen = true;
      return;
    }
    this.persistAssignments();
  }

  /** Proceed with the save after the remove-users confirmation. */
  confirmSaveAssignments(): void {
    this.removeConfirmOpen = false;
    this.persistAssignments();
  }
  cancelRemoveConfirm(): void {
    this.removeConfirmOpen = false;
  }

  private removedCount(role: RoleRow): number {
    const prev = this.assignedByRole.get(role.id) ?? new Set<string>();
    let removed = 0;
    prev.forEach((k) => {
      if (!this.assignSelected.has(k)) removed++;
    });
    return removed;
  }

  private persistAssignments(): void {
    const role = this.assignRole;
    if (!role) {
      this.assignOpen = false;
      return;
    }
    const selected = new Set(this.assignSelected);
    const prev = this.assignedByRole.get(role.id) ?? new Set<string>();
    let added = 0;
    let removed = 0;
    selected.forEach((k) => {
      if (!prev.has(k)) added++;
    });
    prev.forEach((k) => {
      if (!selected.has(k)) removed++;
    });

    const onDone = (failed: boolean) => {
      this.assignedByRole.set(role.id, selected);
      this.assignOpen = false;
      this.assignSaving = false;
      if (failed) {
        this.showToast('Saved locally, but the server could not be reached.', true);
      } else if (added || removed) {
        this.showToast(this.assignmentSummary(role.label, added, removed));
      }
    };

    if (this.workflowTemplateId != null && this.assignmentId != null) {
      this.assignSaving = true;
      const users = [...selected].map((k) => this.keyToUser(k));
      this.workflowService
        .setRoleUsers(this.workflowTemplateId, this.assignmentId, role.roleId, users)
        .subscribe({
          next: () => onDone(false),
          error: (err) => {
            // 400 = server rejection (e.g. already in another role); show it, keep dialog open.
            if (err?.status === 400) {
              this.assignSaving = false;
              this.showToast(err?.error?.message || 'Could not save assignments.', true);
            } else {
              onDone(true);
            }
          },
        });
      return;
    }
    onDone(false);
  }

  private assignmentSummary(roleLabel: string, added: number, removed: number): string {
    if (added && removed) return `${roleLabel}: ${added} added, ${removed} removed.`;
    if (added) return `${added} ${added === 1 ? 'user' : 'users'} added to ${roleLabel}.`;
    return `${removed} ${removed === 1 ? 'user' : 'users'} removed from ${roleLabel}.`;
  }

  cancelAssignments(): void {
    this.assignOpen = false;
    this.removeConfirmOpen = false;
  }

  /** ---------- Deactivate / activate confirmation dialog ---------- */

  openConfirm(role: RoleRow): void {
    this.menuRoleId = null;
    this.confirmRole = role;
    this.confirmOpen = true;
  }

  confirmToggle(): void {
    const role = this.confirmRole;
    if (!role) return;
    this.roleRegistry.setActive(role.id, !role.active).subscribe({
      next: () => {
        this.refresh();
        this.confirmOpen = false;
      },
    });
  }

  cancelConfirm(): void {
    this.confirmOpen = false;
  }
}
